import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import Cookie from '@wetransfer/concorde-cookie';

import Browser from '@wetransfer/concorde-browser';

import Config from '@app/config';
import Store from '@app/store';

import I18n from '@module/i18n';
import Analytics from '@module/analytics';

import StaticPage from '@component/panel/content';
import SignupForm from '@component/form/signup';
import PlusCustomisation from '@app/page/plus/customisation';
import FeatureFlagsHelper from '@app/helper/featureflags';

let pageLoad = true;
let context;

const renderSignupForm = (container, isNewPlusPage, setReturnPath) => {
  const { user, errors, route, pending } = Store.getState();
  const signupForm = (
    <Provider store={Store}>
      <SignupForm
        route={route}
        user={user}
        errors={errors}
        pending={pending}
        isNewPlusPage={isNewPlusPage}
        setReturnPath={setReturnPath}
      />
    </Provider>
  );

  ReactDOM.render(signupForm, container);
};

const renderCustomisation = (container) => {
  const { session } = Store.getState();
  const previews = Config.curatedPreviews.desktop;
  const startingIndex = Math.floor(Math.random() * Math.floor(previews.length));
  const customisation = (
    <PlusCustomisation
      previews={previews}
      mobilePreview={Config.curatedPreviews.mobile}
      start={startingIndex}
      domain={session.domain}
    />
  );

  ReactDOM.render(customisation, container);
};

const removeElement = (selector) => {
  if (!document.querySelector(selector)) {
    return;
  }
  document.querySelector(selector).remove();
};

const contentElementClickHandler = (event, instance, formElement) => {
  if (!Browser.isMobile) {
    // Scroll to form
    event.preventDefault();
    event.stopPropagation();

    const url = new URL(event.target.href);
    window.history.replaceState(null, null, url.href);

    instance.scrollTo(formElement);
  }
};

const pricingFormat = (pricing) => {
  if (
    typeof pricing.total !== 'number' ||
    typeof pricing.currency !== 'string'
  ) {
    return null;
  }
  const sign = I18n.t(`number.currency.${pricing.currency.toUpperCase()}`);
  const amount = I18n.number(pricing.total, { precision: 2 });
  return `${sign}${amount}`;
};

export const plusPage = (instance) => {
  const updatePrice = (selector, pricing) => {
    const nodes = instance.node.querySelectorAll(selector);

    if (nodes && nodes.length && pricing) {
      const price = pricingFormat(pricing);
      if (!price) {
        return;
      }
      nodes.forEach((el) => {
        el.innerText = price;
      });
    }
  };

  // We want to be updated with store updates
  instance.subscribeToStore = true;

  const { route, ui } = Store.getState();
  if (ui && ui.subscriptionPricing) {
    updatePrice('.plus__price--month', ui.subscriptionPricing.month);
    updatePrice('.plus__price--year', ui.subscriptionPricing.year);
  }

  // Get our DOM elements
  const formElement = instance.node.querySelector('#plus__form');

  // The form element won't be in the page when there is no logged in user
  if (!formElement) return;

  const infoElement = instance.node.querySelector('#plus__info');
  const signupButtons = instance.node.querySelectorAll('.button__signup');
  const customisationElement = instance.node.querySelector(
    '#plus-new__customisation'
  );

  if (context !== route.context) {
    pageLoad = true;
  }
  context = route.context;

  // Hide content if we want form only and request flag when we don't want the form
  if ((route.context === 'plusform' || route.query.coupon) && infoElement) {
    infoElement.style.display = 'none';
    sessionStorage.setItem('plusform', true);
  } else {
    sessionStorage.removeItem('plusform');
  }

  if (!Browser.isMobile || route.context === 'plusform' || route.query.coupon) {
    renderSignupForm(
      formElement,
      FeatureFlagsHelper.unlimitedTransfers,
      instance.setReturnPath
    );
  } else {
    ReactDOM.unmountComponentAtNode(formElement);
  }

  // Only once during page load.
  if (pageLoad) {
    pageLoad = false;

    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signup Form View',
      channel: Cookie.get('wt_trk') || undefined,
    });

    Analytics.trackStructEvent({
      category: 'plus',
      action: 'show_sign_up',
    });
  }

  // Render customisation on new plus page
  if (FeatureFlagsHelper.unlimitedTransfers && customisationElement) {
    renderCustomisation(customisationElement);
  }
  if (signupButtons) {
    signupButtons.forEach(function(button) {
      button.addEventListener('click', (e) =>
        contentElementClickHandler(e, instance, formElement)
      );
    });
  }

  // When we navigate away from the page, cleanup listeners
  instance.onCleanup = () => {
    if (signupButtons) {
      signupButtons.forEach((button) => {
        button.removeEventListener('click', contentElementClickHandler);
      });
    }

    ReactDOM.unmountComponentAtNode(formElement);
    if (FeatureFlagsHelper.unlimitedTransfers) {
      ReactDOM.unmountComponentAtNode(customisationElement);
    }
    pageLoad = true;
    removeElement('.plus__content-social-proof-container');
  };
};

StaticPage.configure('/plus', plusPage);
